import { useParams, useLocation } from 'react-router-dom';
import {
  IconLoader,
  usePrevious,
  PermissionEnum,
  ButtonPrimary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setMatchSelected } from './store/slices';
import NewApplicationInfoTop from '../common/new-application-info-top';
import {
  OnSubmitType,
  ApplicationType,
  NewApplicationMainRepresentativeInfoTypes,
} from '../types';
import useApplication from './hooks/use-application';
import useQuestionnarie from './hooks/use-questionnarie';
import ApplicationSteps from './parts/steps';
import ApplicationWrapper from './parts/application-wrapper';

import cn from '../common/styles.module.css';

const NewApplicationMainRepresentativeInfo = ({
  isCompanyOfficial,
}: NewApplicationMainRepresentativeInfoTypes) => {
  const { validators, applicationChat } = useAppSelector(
    (state) => state.newApplications.list,
  );
  const { clientsActivities, infoDictionariesIsLoading } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { sourceOfWealth, sourceOfIncome, sourceOfWealthForIndividual } =
    useAppSelector((state) => state.newApplications.individualInfo);
  const {
    newApplicationInfo,
    hintVisible,
    systemLogs,
    stepsStatus: applicationStepsStatus,
    documents,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    questionnarie,
    changeLogs,
    changeLogsPagination,
    crsAndFatca,
    questionnarieId,
    affilatedCompanies,
  } = useAppSelector((state) => state.newApplications.mainRepresentative);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const prevApplicationId = usePrevious(applicationId);

  const {
    questionnarieFields,
    crsAndFatcaValues,
    haveSecondPassport,
    affilatedCompaniesValues,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveCRSAndFatca,
    onSaveAffilatedCompanies,
    questionnarieSetValue,
    questionnarieIsFetching,
    questionnarieIsLoading,
    createCrsAndFatcaIsProgress,
    updateCrsAndFatcaInProgress,
    createAffilatedCompanyInProgress,
    updateAffilatedCompanyInProgress,
    questionnarieClearErrors,
    setHaveSecondPassport,
    questionnarieControl,
    questionnarieErrors,
    setAffilatedCompaniesValues,
    affilatedCompaniesIsLoading,
    affilatedCompaniesIsFetching,
    deleteAffilatedCompanyInProgress,
    onDeleteAffilatedCompany,
    selectedAffilatedCompanyId,
    crsAndFatcaIsLoading,
    crsAndFatcaIsFetching,
    deleteCrsAndFatcaInProgress,
    onDeleteCRSAndFatca,
    setCrsAndFatcaValues,
    selectedCrsAndFatcaId,
    questionnarieIsDirty,
    defaultQuestionnarieFormValues,
  } = useQuestionnarie({
    affilatedCompanies,
    crsAndFatca,
    questionnarieId,
    newApplicationInfo,
    questionnarie,
    sourceOfWealth,
    activePermissions,
    sourceOfIncome,
    sourceOfWealthForIndividual,
  });

  const {
    userCreated,
    isLoading,
    isFetching,
    applicationLoadErrorVisible,
    setStatusChangeError,
    getApplicationDocuments,
    onSubmit,
    requestInProgress,
    questionnarieUpdateInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    onCreateCustomer,
    createinProgress,
    setError,
    clearErrors,
    control,
    errors,
    setValue,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    updateError,
    updateQuestionnarieError,
    agreementsIsLoading,
    agreementsFetching,
  } = useApplication({
    validators,
    clientsActivities,
    applicationStepsStatus,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    newApplicationInfo,
    questionnarieFields,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
    affilatedCompaniesValues,
    applicationId,
    activePermissions,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveCRSAndFatca,
    onSaveAffilatedCompanies,
    prevApplicationId,
    questionnarieSetValue,
    defaultQuestionnarieFormValues,
  });

  let content;

  if (!userCreated && (isLoading || isFetching)) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (applicationLoadErrorVisible) {
    content = (
      <div className={cn.applicationLoadError}>
        <div className={cn.applicationLoadErrorTitle}>
          Something went wrong. Reload page
        </div>
        <ButtonPrimary
          text="Reload"
          className={cn.reloadButton}
          onClick={() => window.location.reload()}
          bold
        />
      </div>
    );
  } else {
    content = (
      <div className={cn.newApplicationInfoContent}>
        <NewApplicationInfoTop
          setStatusChangeError={setStatusChangeError}
          getApplicationDocuments={getApplicationDocuments}
          setSuccessMessageVisible={() => {}}
          newApplicationInfo={newApplicationInfo}
          stepsStatus={applicationStepsStatus}
          setMatchSelected={(data) => dispatch(setMatchSelected(data))}
          applicationType={ApplicationType.COMPANY_OFFICIAL}
          systemLogs={systemLogs}
          applicationId={applicationId}

          onSubmit={onSubmit as OnSubmitType}
          requestInProgress={
            requestInProgress ||
            questionnarieUpdateInProgress ||
            createCrsAndFatcaIsProgress ||
            updateCrsAndFatcaInProgress ||
            createAffilatedCompanyInProgress ||
            updateAffilatedCompanyInProgress
          }
          fields={fields}
          questionnarieFields={questionnarieFields}
          defaultFormValues={defaultFormValues}
          defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
          isCompanyOfficial={isCompanyOfficial}
          statusChangeError={statusChangeError}
          isEditable={activePermissions?.includes(
            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
          )}
          createAvailable={activePermissions?.includes(
            PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
          )}
          onCreate={() => onCreateCustomer()}
          createInProgress={createinProgress}
          canSelectResponsible={activePermissions?.includes(
            PermissionEnum.USERS_LIST,
          )}
          title={
            newApplicationInfo
              ? `${newApplicationInfo.firstName} ${newApplicationInfo.lastName}`
              : 'New Application'
          }
          applicationChat={applicationChat ? applicationChat[0] : undefined}
          setError={setError}
          clearErrors={clearErrors}
          questionnarieSetError={questionnarieSetError}
          questionnarieClearErrors={questionnarieClearErrors}
          changeLogs={changeLogs}
          changeLogsPagination={changeLogsPagination}
          onSaveCrsAndFatca={onSaveCRSAndFatca}
          crsAndFatcaValues={crsAndFatcaValues
            ?.filter((item) => item.residenceCountryId)
            ?.map((item) => ({
              residenceCountryId: item?.residenceCountryId || '',
              taxIdentificationNumber: item?.taxIdentificationNumber || '',
              noTinReason: item?.noTinReason,
            }))}
          crsAndFatca={crsAndFatca?.map((item) => ({
            residenceCountryId: item?.residenceCountryId,
            taxIdentificationNumber: item?.taxIdentificationNumber || '',
            noTinReason: item?.noTinReason || '',
          }))}
          stepOneBlockLoadingInProgress={
            createAffilatedCompanyInProgress || updateAffilatedCompanyInProgress
          }
          onSaveAffilatedCompany={onSaveAffilatedCompanies}
          setHaveSecondPassport={setHaveSecondPassport}
          haveSecondPassport={haveSecondPassport}
          affilatedCompaniesValues={affilatedCompaniesValues
            ?.filter(
              (item) =>
                item.name &&
                item.registrationNumber &&
                item.mainActivities &&
                item.position &&
                item.countryId &&
                item.percentageOfOwnership,
            )
            ?.map((item) => ({
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            }))}
          affilatedCompanies={affilatedCompanies?.map((item) => ({
            name: item.name,
            registrationNumber: item.registrationNumber,
            mainActivities: item.mainActivities,
            position: item.position,
            countryId: item.countryId,
            percentageOfOwnership: Number(item.percentageOfOwnership),
          }))}
          updateError={updateError}
          updateQuestionnarieError={updateQuestionnarieError}
          infoDictionariesIsLoading={
            infoDictionariesIsLoading ||
            questionnarieIsLoading ||
            (!questionnarie && questionnarieIsFetching)
          }
        />
        <ApplicationSteps
          applicationStepsStatus={applicationStepsStatus}
          control={control}
          questionnarieControl={questionnarieControl}
          errors={errors}
          questionnarieErrors={questionnarieErrors}
          newApplicationInfo={newApplicationInfo}
          activePermissions={activePermissions}
          clearErrors={clearErrors}
          questionnarieClearErrors={questionnarieClearErrors}
          questionnarieSetValue={questionnarieSetValue}
          documents={documents}
          fields={fields}
          questionnarieFields={questionnarieFields}
          sourceOfWealth={sourceOfWealth}
          sourceOfIncome={sourceOfIncome}
          sourceOfWealthForIndividual={sourceOfWealthForIndividual}
          affilatedCompaniesValues={affilatedCompaniesValues}
          setAffilatedCompaniesValues={setAffilatedCompaniesValues}
          affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
          affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
          deleteAffilatedCompanyInProgress={deleteAffilatedCompanyInProgress}
          onDeleteAffilatedCompany={onDeleteAffilatedCompany}
          selectedAffilatedCompanyId={selectedAffilatedCompanyId}
          applicationId={applicationId}
          crsAndFatcaIsLoading={crsAndFatcaIsLoading}
          crsAndFatcaIsFetching={crsAndFatcaIsFetching}
          deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
          onDeleteCRSAndFatca={onDeleteCRSAndFatca}
          crsAndFatcaValues={crsAndFatcaValues}
          setCrsAndFatcaValues={setCrsAndFatcaValues}
          setHaveSecondPassport={setHaveSecondPassport}
          haveSecondPassport={haveSecondPassport}
          selectedCrsAndFatcaId={selectedCrsAndFatcaId}
          setValue={setValue}
          search={search}
          ongoingMonitoring={ongoingMonitoring}
          ongoingCheckingStatus={ongoingCheckingStatus}
          agreements={agreements}
          agreementsIsLoading={agreementsIsLoading || agreementsFetching}
          questionnarieIsLoading={
            questionnarieIsLoading ||
            (!questionnarie && questionnarieIsFetching)
          }
        />
      </div>
    );
  }

  return (
    <ApplicationWrapper
      isDirty={isDirty}
      questionnarieIsDirty={questionnarieIsDirty}
      setDirtyPopupVisible={setDirtyPopupVisible}
      hintVisible={hintVisible}
      newApplicationInfo={newApplicationInfo}
      dirtyPopupVisible={dirtyPopupVisible}
    >
      {content}
    </ApplicationWrapper>
  );
};

export default NewApplicationMainRepresentativeInfo;
